import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import {
  BACKGROUND_TASK_WORK,
  createBackgroundTaskStore,
  type BackgroundTask,
  type BackgroundTaskStore,
} from '../runtime/background-tasks';
import { panelDurableWork, type DurableWorkScope } from '../runtime/durable-work';

const BackgroundTasksContext = createContext<BackgroundTaskStore | null>(null);
const NO_TASKS: readonly BackgroundTask[] = Object.freeze([]);
const noSubscription = () => () => undefined;

/** How often a waiting result is checked against its server expiry. */
const EXPIRY_SWEEP_MS = 60_000;

/**
 * One background-task holder per restaurant session, mounted above every screen
 * (beside the session inbox): a screen that starts a task may unmount and the task
 * keeps its answer. A new principal or restaurant gets a new, empty holder.
 */
export function BackgroundTasksProvider({
  scope,
  store: injected,
  children,
}: {
  readonly scope?: DurableWorkScope;
  /** Specs and the proof gallery inject their own store. */
  readonly store?: BackgroundTaskStore;
  readonly children: ReactNode;
}) {
  const store = useMemo(
    () => injected ?? createBackgroundTaskStore(scope ? { work: panelDurableWork.open(scope, BACKGROUND_TASK_WORK) } : {}),
    [injected, scope],
  );
  useEffect(() => {
    store.expire();
    const timer = window.setInterval(() => store.expire(), EXPIRY_SWEEP_MS);
    return () => window.clearInterval(timer);
  }, [store]);
  return <BackgroundTasksContext.Provider value={store}>{children}</BackgroundTasksContext.Provider>;
}

/** `null` outside a restaurant session (and in specs that mount a screen alone). */
export function useBackgroundTaskStore(): BackgroundTaskStore | null {
  return useContext(BackgroundTasksContext);
}

export function useBackgroundTasks(): readonly BackgroundTask[] {
  const store = useBackgroundTaskStore();
  return useSyncExternalStore(
    store ? store.subscribe : noSubscription,
    store ? store.getSnapshot : () => NO_TASKS,
  );
}

/** The task the chip's «Ver» asked a screen to open, once. */
export function useBackgroundTaskOpenRequest(): string | null {
  const store = useBackgroundTaskStore();
  return useSyncExternalStore(store ? store.subscribe : noSubscription, store ? store.openRequest : () => null);
}
